import { UserProgress } from '../types/progress';

/**
 * Achievement interface
 */
export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  requirement: (progress: UserProgress) => boolean;
  rewards?: {
    skillPoints?: Partial<UserProgress['skillLevels']>;
    unlockedContent?: string[];
  };
}

/**
 * List of all achievements in the game
 */
export const achievements: Achievement[] = [
  {
    id: 'first_mission',
    title: 'First Assignment',
    description: 'Complete your first mission',
    icon: '🎯',
    requirement: (progress) => progress.statistics.totalMissionsCompleted >= 1,
  },
  {
    id: 'mission_streak',
    title: 'On a Roll',
    description: 'Complete 3 missions in a row',
    icon: '🔥',
    requirement: (progress) => progress.statistics.totalMissionsCompleted >= 3,
    rewards: {
      skillPoints: {
        coordinates: 1,
      },
    },
  },
  {
    id: 'perfect_score',
    title: 'Perfect Execution',
    description: 'Complete a mission with 100% accuracy',
    icon: '✨',
    requirement: (progress) => progress.statistics.bestScore >= 100,
    rewards: {
      skillPoints: {
        coordinates: 1,
        basicMoves: 1,
      },
    },
  },
  {
    id: 'rookie_agent',
    title: 'Rookie Agent',
    description: 'Reach Rookie Agent rank',
    icon: '🔰',
    requirement: (progress) => 
      progress.skillLevels.coordinates >= 2 && 
      progress.skillLevels.basicMoves >= 1,
  },
  {
    id: 'field_agent',
    title: 'Field Agent',
    description: 'Reach Field Agent rank',
    icon: '🏅',
    requirement: (progress) => 
      progress.skillLevels.coordinates >= 3 && 
      progress.skillLevels.basicMoves >= 2 &&
      progress.skillLevels.sequences >= 1,
  },
];

/**
 * Get all achievements
 */
export const getAllAchievements = (): Achievement[] => {
  return achievements;
};

/**
 * Get unlocked achievements for a user
 * 
 * @param progress User progress data
 * @returns Array of unlocked achievements
 */
export const getUnlockedAchievements = (progress: UserProgress): Achievement[] => {
  return achievements.filter(achievement => achievement.requirement(progress));
};

/**
 * Check if an achievement is unlocked
 * 
 * @param achievementId Achievement ID to check
 * @param progress User progress data
 * @returns True if the achievement is unlocked
 */
export const isAchievementUnlocked = (achievementId: string, progress: UserProgress): boolean => {
  const achievement = achievements.find(a => a.id === achievementId);
  if (!achievement) {
    return false;
  }
  return achievement.requirement(progress);
};

/**
 * Check for newly unlocked achievements
 * 
 * @param progress User progress data
 * @param previouslyUnlocked Array of previously unlocked achievement IDs
 * @returns Array of newly unlocked achievements
 */
export const getNewlyUnlockedAchievements = (
  progress: UserProgress,
  previouslyUnlocked: string[]
): Achievement[] => {
  // Ensure we have a valid array of previously unlocked achievements
  const safeUnlockedIds = Array.isArray(previouslyUnlocked) ? previouslyUnlocked : [];
  
  // Get currently unlocked achievements and ensure it's an array
  const unlockedAchievements = getUnlockedAchievements(progress) || [];
  
  // Filter out previously unlocked achievements
  return unlockedAchievements.filter(
    achievement => !safeUnlockedIds.includes(achievement.id)
  );
};

export default {
  getAllAchievements,
  getUnlockedAchievements,
  isAchievementUnlocked,
  getNewlyUnlockedAchievements,
}; 